import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { ref as dbRef, get, push } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { useAuth } from "../context/AuthContext";
import { ArrowLeftRight, Plus, X, Camera, AlertCircle, Package } from "lucide-react";

const CATEGORIES = [
  "Books", "Electronics", "Cycles & Vehicles", "Room Essentials",
  "Clothing & Uniforms", "Sports & Fitness", "Musical Instruments",
  "Study Tools", "Furniture", "Kitchen Items", "Art & Craft", "Any / Open to offers"
];

function SwapCard({ swap }) {
  const waMsg = encodeURIComponent(
    `Hi! I saw your swap offer on CampusSwap. You're offering "${swap.offeringTitle}" and want "${swap.wantTitle}". I'd like to propose a swap!`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-xl bg-gray-100 flex-shrink-0 overflow-hidden">
          {swap.imageUrl
            ? <img src={swap.imageUrl} alt={swap.offeringTitle} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center"><Package size={28} className="text-gray-300" /></div>
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">SWAP</span>
            {swap.sellerVerified && <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full">✓ Verified</span>}
          </div>
          <p className="font-semibold text-gray-800 text-sm line-clamp-1">Offering: {swap.offeringTitle}</p>
          <div className="flex items-center gap-1 mt-1">
            <ArrowLeftRight size={12} className="text-blue-400 flex-shrink-0" />
            <p className="text-xs text-blue-600 font-medium line-clamp-1">Wants: {swap.wantTitle}</p>
          </div>
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{swap.postedByName} · {swap.college}</p>
        </div>
      </div>
      {swap.description && (
        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{swap.description}</p>
      )}
      <div className="flex gap-2 mt-3">
        <a
          href={`https://wa.me/91${swap.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
          target="_blank" rel="noreferrer"
          className="flex-1 bg-green-500 text-white text-sm py-2 rounded-xl font-semibold text-center hover:bg-green-600"
        >
          Propose Swap
        </a>
      </div>
    </div>
  );
}

export default function Swap() {
  const { currentUser, userProfile } = useAuth();
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    offeringTitle: "", offeringCategory: "", description: "",
    wantTitle: "", wantCategory: "", whatsapp: userProfile?.phone || ""
  });

  useEffect(() => { fetchSwaps(); }, []);

  async function fetchSwaps() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "swaps"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        const open = all
          .filter(s => s.status === "open")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setSwaps(open);
      } else {
        setSwaps([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  function handleImage(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("Image must be under 5MB."); return; }
    setImageFile(f);
    setPreview(URL.createObjectURL(f));
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.offeringTitle?.trim() || form.offeringTitle.trim().length < 5) return setError("Item name must be at least 5 characters.");
    if (!form.offeringCategory) return setError("Select a category for what you're offering.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      await push(dbRef(db, "swaps"), {
        offeringTitle: form.offeringTitle.trim(),
        offeringCategory: form.offeringCategory,
        description: form.description.trim(),
        wantTitle: form.wantTitle.trim() || form.wantCategory,
        wantCategory: form.wantCategory,
        imageUrl,
        whatsapp: form.whatsapp.trim(),
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        sellerVerified: userProfile?.verified || false,
        status: "open",
        createdAt: new Date().toISOString(),
      });
      setForm({ offeringTitle: "", offeringCategory: "", description: "", wantTitle: "", wantCategory: "", whatsapp: userProfile?.phone || "" });
      setImageFile(null); setPreview(null);
      setShowForm(false);
      fetchSwaps();
    } catch (err) {
      setError("Failed to post. Try again.");
      
    }
    setPosting(false);
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Swap Items</h1>
            <p className="text-blue-200 text-xs mt-0.5">Give one, get one — no money needed</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-blue-600 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Post Swap
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="px-4 -mt-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs font-bold text-gray-600 mb-2">HOW IT WORKS</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
              <span className="text-center">Post what you have</span>
            </div>
            <ArrowLeftRight size={16} className="text-gray-300 flex-shrink-0" />
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">2</div>
              <span className="text-center">Say what you want</span>
            </div>
            <ArrowLeftRight size={16} className="text-gray-300 flex-shrink-0" />
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">3</div>
              <span className="text-center">Exchange on campus</span>
            </div>
          </div>
        </div>
      </div>

      {/* Swap List */}
      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />)
        ) : swaps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <ArrowLeftRight size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No swaps posted yet</p>
            <p className="text-gray-400 text-sm mt-1">Be the first to post a swap offer!</p>
            <button onClick={() => setShowForm(true)}
              className="mt-4 bg-blue-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post First Swap
            </button>
          </div>
        ) : swaps.map(s => <SwapCard key={s.id} swap={s} />)}
      </div>

      {/* Post Swap Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Post a Swap</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-4 pb-24">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={16} />{error}
                </div>
              )}
              <div>
                <p className="text-sm font-bold text-gray-700 mb-3">📦 What I'm Offering</p>
                <div onClick={() => document.getElementById("swap-img").click()}
                  className="border-2 border-dashed border-blue-300 rounded-xl h-32 flex items-center justify-center cursor-pointer mb-3 overflow-hidden">
                  {preview
                    ? <img src={preview} alt="p" className="h-full w-full object-cover" />
                    : <div className="text-blue-400 text-center"><Camera size={24} className="mx-auto mb-1" /><p className="text-xs">Add photo (optional)</p></div>}
                </div>
                <input id="swap-img" type="file" accept="image/*" onChange={handleImage} className="hidden" />
                <input name="offeringTitle" value={form.offeringTitle} onChange={handleChange} required
                  placeholder="Item title (e.g. Engineering Drawing Set)"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2" />
                <select name="offeringCategory" value={form.offeringCategory} onChange={handleChange} required
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700">
                  <option value="">Category</option>
                  {CATEGORIES.filter(c => c !== "Any / Open to offers").map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                  placeholder="Condition, edition, anything else..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none mt-2" />
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-bold text-gray-700 mb-3">🔄 What I Want in Return</p>
                <input name="wantTitle" value={form.wantTitle} onChange={handleChange}
                  placeholder="Item name (e.g. DBMS Notes, any laptop stand...)"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2" />
                <select name="wantCategory" value={form.wantCategory} onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700">
                  <option value="">Category (optional)</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your WhatsApp *</label>
                <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                  placeholder="10-digit number"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <button type="submit" disabled={posting}
                className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50">
                {posting ? "Posting..." : "Post Swap Offer"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
